import React from 'react';
import styled from 'styled-components';
import { recognizeReferencePoint } from '../../functions/utils/recognizeReferencePoint';
import { LauncherWindowData, useLauncherWindowStore } from '../../hooks/stores/launcherWindowStore';
import { UIData, useUIStore } from '../../hooks/stores/uiStore';
import { useScene } from '../../context/sceneContext';

const Container = styled.div`
  pointer-events: none;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9998;
`;

const GuideLine = styled.div<{ $isHorizontal: boolean }>`
  position: absolute;
  background: transparent;
  opacity: 0.8;
  z-index: 9999;
  
  ${props => props.$isHorizontal ? `
    width: 100%;
    height: 2px;
    border-top: 1px dashed var(--warn-1);
  ` : `
    width: 2px;
    height: 100%;
    border-left: 1px dashed var(--warn-1);
  `}
`;

const DimensionLabel = styled.div`
  pointer-events: none;
  position: absolute;
  padding: 2px 5px;
  border-radius: 5px;
  background-color: var(--neutral-2);
  color: var(--warn-1);
  font-family: 'Inter';
  font-size: 12px;
  white-space: nowrap;
  box-shadow: 0 0 5px #000;
  transform: translate(-50%, -50%);
  z-index: 10000;
`;

export const AbsolutePositionGuides = React.memo(() => {
  const scene = useScene();
  const [UIData] = useUIStore<UIData>();
  const [launcherWindowData] = useLauncherWindowStore<LauncherWindowData>();


  if (UIData.selectedElementIDs.length === 0 || !UIData.elementHandle.isVisible) {
    return null;
  }

  if (!UIData.showAbsoluteGuides) return null;

  const { pos: handlePos, width: handleWidth, height: handleHeight } = UIData.elementHandle;
  const selectedElementID = UIData.selectedElementIDs[0];
  const elementData = scene.getElementData(
    selectedElementID,
    {
      left: undefined,
      top: undefined,
      bottom: undefined,
      right: undefined,
    },
    'props.class'
  );
  const referencePoint = recognizeReferencePoint(elementData);

  // Calculate guide positions based on reference point
  const getGuidePositions = () => {
    // Default to left/top if reference point is not defined
    const refX = referencePoint.x || 'left';
    const refY = referencePoint.y || 'top';

    const guideX = refX === 'right' 
      ? handlePos.x + handleWidth  // Show guide at right edge
      : handlePos.x;               // Show guide at left edge

    const guideY = refY === 'bottom'
      ? handlePos.y + handleHeight // Show guide at bottom edge
      : handlePos.y;               // Show guide at top edge

    // Calculate actual coordinates for labels
    const x = refX === 'right' 
      ? launcherWindowData.windowWidth - guideX
      : guideX;
    
    const y = refY === 'bottom'
      ? launcherWindowData.windowHeight - guideY
      : guideY;

    return { 
      guideX, 
      guideY, 
      x, 
      y,
      showXLabelAtTop: refY === 'top',
      showYLabelAtLeft: refX === 'left'
    };
  };

  const { guideX, guideY, x, y, showXLabelAtTop, showYLabelAtLeft } = getGuidePositions();

  return (
    <Container>
      {/* Vertical guide line - from reference point */}
      <GuideLine 
        $isHorizontal={false} 
        style={{ 
          left: guideX,
          top: referencePoint.y === 'bottom' ? guideY : 0,
          height: referencePoint.y === 'bottom' ? launcherWindowData.windowHeight - guideY : guideY,
          opacity: 0.8
        }} 
      />
      
      {/* Horizontal guide line - from reference point */}
      <GuideLine 
        $isHorizontal={true}
        style={{ 
          left: referencePoint.x === 'right' ? guideX : 0,
          top: guideY,
          width: referencePoint.x === 'right' ? launcherWindowData.windowWidth - guideX : guideX,
          opacity: 0.8
        }} 
      />

      {/* X coordinate label */}
      <DimensionLabel
        style={{
          display: x === 0 ? 'none' : 'block',  // Hide when 0
          left: referencePoint.x === 'right' ? handlePos.x + handleWidth - 5 : handlePos.x - 5,
          top: referencePoint.y === 'bottom' 
            ? launcherWindowData.windowHeight + 10
            : -25,
          transform: 'translate(-100%, 0)'
        }}
      >
        x: {Math.round(x)}
      </DimensionLabel>

      {/* Y coordinate label */}
      <DimensionLabel
        style={{
          display: y === 0 ? 'none' : 'block',  // Hide when 0
          left: referencePoint.x === 'right' 
            ? launcherWindowData.windowWidth + 5
            : -5,
          top: referencePoint.y === 'bottom' ? handlePos.y + handleHeight - 5 : handlePos.y - 5,
          transform: referencePoint.x === 'right' 
            ? 'translate(0, -100%)'
            : 'translate(-100%, -100%)'
        }}
      >
        y: {Math.round(y)}
      </DimensionLabel>
    </Container>
  );
});